import config from 'config'
import { marketplace, inputParsers } from '@sellpy/commons'
import { activeAbTests, getABTestFraction, getSanityAbTestFraction } from '../analytics/abTest'
import { getCachedPersonalisationValues } from '../../client/personalisation/personalisationValuesQuery'
import { getAlgoliaLocaleApplication } from '../lib/algolia'
import { getRegion } from '../region/region'
import { locale } from '../region/locale'

const createDataLayerEvent = (event) => {
  const personalisationValues = getCachedPersonalisationValues()
  const personalisedBaseCategory = personalisationValues
    ? personalisationValues.baseCategoryScores[0]?.baseCategory ?? 'not assigned'
    : undefined

  const eventWithData = Object.assign(event, {
    ab_tests: activeAbTests(),
    ga4_id: config.ga4Id[getRegion()],
    region: getRegion(),
    clientAbTestFractionGroup: parseFloat(getABTestFraction()).toFixed(4),
    clientSanityAbTestFractionGroup: parseFloat(getSanityAbTestFraction()).toFixed(4),
    aw_id: config.googleAdwordsId[getRegion()],
    algolia_app_id: getAlgoliaLocaleApplication(locale).client.appId,
    client_current_commit: process.env.COMMIT_HASH?.slice(0, 8),
    user_base_category: personalisedBaseCategory?.toLowerCase()
  })
  return eventWithData
}

const dataLayerPush = (event) => {
  if (typeof window === 'undefined') {
    return
  }

  const dataLayerName = 'gtmDataLayer'

  const eventWithData = createDataLayerEvent(event)

  window[dataLayerName] && window[dataLayerName].push(eventWithData)
}

const trackPermissionAnalytics = ({ permissionValue }) => {
  dataLayerPush({
    event: 'permission_analytics',
    permission_analytics: permissionValue
  })
}

const trackPermissionExternalMarketing = ({ permissionValue }) => {
  dataLayerPush({
    event: 'permission_external_marketing',
    permission_external_marketing: permissionValue,
    allow_ad_personalization_signals: permissionValue
  })
}

const trackPermissionEmailMarketing = ({ permissionValue }) => {
  dataLayerPush({
    event: 'permission_email_marketing',
    permission_email_marketing: permissionValue
  })
}

const trackSetUserId = ({ id }) => {
  inputParsers.hashStringSHA256(id).then((hashedId) => {
    dataLayerPush({
      event: 'set_user_id',
      user_id: id,
      user_id_hsh: hashedId
    })
  })
}

const trackUnsetUserId = () => {
  dataLayerPush({
    user_id: undefined,
    user_id_hsh: undefined
  })
}

const trackSetUserEmail = ({ email }) => {
  inputParsers.hashStringSHA256(email).then((hashedEmail) => {
    dataLayerPush({
      event: 'set_email',
      email_hsh: hashedEmail,
      sha256_hash_hex_user_email: hashedEmail
    })
  })
}

const trackUnsetUserEmail = () => {
  dataLayerPush({
    email_hsh: undefined,
    sha256_hash_hex_user_email: undefined
  })
}

const trackSetUserPhone = ({ phone }) => {
  Promise.all([
    inputParsers.hashStringSHA256(inputParsers.normalizePhoneNumber(phone)),
    inputParsers.hashStringSHA256(phone)
  ]).then((hashedPhones) => {
    dataLayerPush({
      event: 'set_phone',
      phone_1_hsh: hashedPhones[0],
      phone_2_hsh: hashedPhones[1]
    })
  })
}

const trackUnsetUserPhone = () => {
  dataLayerPush({
    phone_1_hsh: undefined,
    phone_2_hsh: undefined
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4?hl=en#measure_viewsimpressions_of_productitem_details
const trackItemView = ({ hasProductId, ...item }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_item',
    ecommerce: {
      items: [item]
    },
    has_product_id: hasProductId
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4?hl=en#measure_viewsimpressions_of_productitem_details
const trackCartView = ({ checkoutType, items, deliveryPromise }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_cart',
    checkout_type: checkoutType,
    ecommerce: {
      items
    },
    delivery_promise: deliveryPromise
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4?hl=en#measure_productitem_list_clicks
const trackItemClick = ({ pdpType, location, ...data }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'select_item',
    location,
    ecommerce: {
      items: [
        {
          pdpType,
          ...data
        }
      ]
    }
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_promotion_clicks
const trackPromotionClick = ({ promotionName, segment, location, position, target, ...data }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'select_promotion',
    ecommerce: {
      items: [
        {
          promotion_name: promotionName,
          creative_slot: segment,
          location_id: location
        }
      ]
    },
    position,
    target,
    ...data
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_additions_or_removals_from_a_shopping_cart
const trackAddToCart = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_to_cart',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_additions_or_removals_from_a_shopping_cart
const trackRemoveFromCart = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'remove_from_cart',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_additions_or_removals_from_a_shopping_cart
const trackGiftCardAddToCart = ({ amount, currency }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_to_cart',
    ecommerce: {
      items: [
        {
          item_name: 'gift_card',
          price: amount,
          currency: currency
        }
      ]
    }
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_additions_or_removals_from_a_shopping_cart
const trackGiftCardRemoveFromCart = ({ amount, currency }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'remove_from_cart',
    ecommerce: {
      items: [
        {
          item_name: 'gift_card',
          price: amount,
          currency: currency
        }
      ]
    }
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_a_checkout
const trackBeginCheckout = ({ items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'begin_checkout',
    ecommerce: {
      items: items.map((item) => ({
        item_name: item.name,
        price: item.price,
        currency: item.currency
      }))
    }
  })
}

const trackBeginCheckoutV2 = ({
  checkoutType,
  loginCheck,
  items,
  noExpressBags,
  noOfBags,
  hasExpressOption
}) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'begin_checkout',
    checkout_type: checkoutType,
    login_check: loginCheck,
    ecommerce: {
      items
    },
    has_express_option: hasExpressOption,
    no_express_bags: noExpressBags,
    no_of_bags: noOfBags
  })
}

const trackCheckoutViewAddEmail = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_add_email_info',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

const trackCheckoutAddEmailInfo = ({ checkoutType, method, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_email_info',
    checkout_type: checkoutType,
    email_method: method,
    ecommerce: {
      items
    }
  })
}

const trackCheckoutAddPromoCode = ({ checkoutType, promoCode, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_coupon',
    checkout_type: checkoutType,
    ecommerce: {
      user_input: promoCode,
      input_accepted: true,
      items
    }
  })
}

const trackCheckoutViewAddAddress = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_add_shipping_address',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

const trackCheckoutAddShippingAddress = ({ checkoutType, addressMethod, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_shipping_address',
    checkout_type: checkoutType,
    ecommerce: {
      address_method: addressMethod,
      items
    }
  })
}

const trackCheckoutViewReviewOrder = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_review_order',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

const trackCheckoutReviewOrder = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'review_order',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

const trackCheckoutViewAddShipping = ({ checkoutType, items, shippingOptions }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_add_shipping_info',
    checkout_type: checkoutType,
    ecommerce: {
      items,
      shipping_options_company: shippingOptions?.map((option) => option.shippingCompany).join(','),
      shipping_options_express: shippingOptions?.map((option) => option.express).join(','),
      shipping_options_tier: shippingOptions?.map((option) => option.shippingTier).join(','),
      shipping_options_price: shippingOptions?.map((option) => option.shippingPrice).join(','),
      shipping_options_currency: shippingOptions?.[0].currency,
      shipping_options_max: shippingOptions ? shippingOptions.length : undefined
    }
  })
}

const trackCheckoutAddShippingInfo = ({
  checkoutType,
  items,
  shippingCompany,
  express,
  shippingTier,
  shippingPrice,
  currency,
  selectedIndex,
  shippingOptions
}) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_shipping_info',
    checkout_type: checkoutType,
    ecommerce: {
      items,
      shipping_company: shippingCompany,
      express,
      shipping_tier: shippingTier,
      shipping: shippingPrice,
      currency,
      index: selectedIndex,
      shipping_options_company: shippingOptions?.map((option) => option.shippingCompany).join(','),
      shipping_options_express: shippingOptions?.map((option) => option.express).join(','),
      shipping_options_tier: shippingOptions?.map((option) => option.shippingTier).join(','),
      shipping_options_price: shippingOptions?.map((option) => option.shippingPrice).join(','),
      shipping_options_currency: shippingOptions?.[0].currency,
      shipping_options_max: shippingOptions ? shippingOptions.length : undefined
    }
  })
}

const trackCheckoutAddP2PShippingInfo = ({ checkoutType, shippingTier }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_p2p_shipping_info',
    checkout_type: checkoutType,
    ecommerce: {
      shipping_tier: shippingTier
    }
  })
}

const trackCheckoutViewPaymentInfo = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_add_payment_info',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

// https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference/events#add_payment_info
const trackCheckoutAddPaymentInfo = ({ checkoutType, items, paymentMethod }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_payment_info',
    checkout_type: checkoutType,
    ecommerce: {
      items,
      payment_type: paymentMethod
    }
  })
}

const trackViewCreditConversion = ({ checkoutType, items }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'view_credit_conversion',
    checkout_type: checkoutType,
    conversion_location: 'cart',
    ecommerce: {
      items,
      currency: marketplace.CURRENCY[getRegion()]
    }
  })
}

const trackOptInAutomaticTransfers = ({
  payoutType,
  updateAutomaticPayouts,
  amount,
  clickSource
}) => {
  dataLayerPush({
    event: 'payout',
    payout_type: payoutType,
    auto_payout_flag: updateAutomaticPayouts,
    amount,
    click_source: clickSource
  })
}

const trackAutomaticPayoutSettings = ({ payoutType, clickSource, settingAction }) => {
  dataLayerPush({
    event: 'automatic_payout_settings',
    payout_type: payoutType,
    setting_action: settingAction,
    setting_source: clickSource
  })
}

const trackCreditConversion = ({
  checkoutType,
  items,
  totalCreditsAvailable,
  creditsConverted
}) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'credit_conversion',
    total_credits_available: totalCreditsAvailable,
    credits_converted: creditsConverted,
    checkout_type: checkoutType,
    conversion_location: 'cart',
    ecommerce: {
      items,
      currency: marketplace.CURRENCY[getRegion()]
    }
  })
}

// https://developers.google.com/tag-manager/ecommerce-ga4#measure_purchases
const trackPurchase = ({
  items,
  paymentId,
  price,
  currency,
  tax,
  shipping,
  coupon,
  marketOrderCount,
  purchaseType,
  stringValue,
  itemIds,
  orderId
}) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'purchase',
    ecommerce: {
      transaction_id: paymentId,
      value: price,
      tax,
      shipping,
      currency,
      coupon,
      market_order_count: marketOrderCount,
      items: items?.map((item) => ({
        item_name: item.name,
        item_id: item.id,
        price: item.price,
        item_brand: item.brand,
        item_category: item.type,
        quantity: 1
      })),
      quantity: items?.length
    },
    purchase_type: purchaseType,
    string_value: stringValue,
    line_items: itemIds?.map((itemId) => ({ product_id: itemId })), // This is for the pinterst pixel
    order_id: orderId
  })
}

const trackPurchaseV2 = ({
  checkoutType,
  currency,
  value,
  totalItemDiscount,
  freightDiscount,
  coupon,
  items,
  shipping,
  marketOrderCount,
  latestPurchaseDate,
  purchaseType,
  itemIds,
  paymentId,
  isExpressAssortment,
  nrOfBags
}) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'purchase',
    checkout_type: checkoutType,
    freight_discount: freightDiscount,
    total_item_discount: totalItemDiscount,
    ecommerce: {
      currency,
      value,
      ...(coupon && { coupon }),
      shipping,
      items,
      market_order_count: marketOrderCount.toString(),
      latest_purchase_date: latestPurchaseDate?.toISOString()
    },
    purchase_type: purchaseType,
    string_value: value.toString(),
    line_items: itemIds?.map((itemId) => ({ product_id: itemId })), // This is for the pinterst pixel
    item_ids: itemIds,
    transaction_id: paymentId,
    has_express_bags: isExpressAssortment,
    no_express_bags: nrOfBags
  })
}

const trackRefund = ({ itemId, paymentId }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'refund',
    ecommerce: {
      transation_id: paymentId,
      items: [
        {
          item_id: itemId
        }
      ]
    }
  })
}

const trackFakeDoorClick = ({ userId, type }) => {
  dataLayerPush({
    event: 'fake_door_click',
    type,
    user_id: userId
  })
}

// https://developers.google.com/gtagjs/reference/ga4-events#login
const trackLogin = ({ method }) => {
  dataLayerPush({
    event: 'login',
    method
  })
}

// https://developers.google.com/gtagjs/reference/ga4-events#login
const trackSignup = ({ method }) => {
  dataLayerPush({
    event: 'sign_up',
    method
  })
}

const trackGiftCardRedeemed = ({ id }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'gift_card_redeemed',
    ecommerce: {
      items: [
        {
          item_id: id
        }
      ]
    }
  })
}

const trackMenuNavigation = ({ label, action }) => {
  dataLayerPush({
    event: 'menu_navigation',
    navigation_value: label,
    navigation_action: action
  })
}

const trackHomeTabNavigation = ({ tab }) => {
  dataLayerPush({
    event: 'homeTabClick',
    tab
  })
}

const trackMapDrawer = ({ action, pickupAvailable }) => {
  dataLayerPush({
    event: 'map_drawer',
    action,
    pickupAvailable
  })
}

const trackOrderPickupButton = ({ location }) => {
  dataLayerPush({
    event: 'order_pickup_button',
    location
  })
}

const trackDropoffButton = () => {
  dataLayerPush({
    event: 'dropoff_button'
  })
}

const trackBuyingNudgeExploreButton = () => {
  dataLayerPush({
    event: 'buying_nudge_explore_button'
  })
}

const trackBuyingNudgeCloseButton = () => {
  dataLayerPush({
    event: 'buying_nudge_close_button'
  })
}

const trackCommencePreparedItemSale = () => {
  dataLayerPush({
    event: 'commence_prepared_item_sale'
  })
}

const trackExtendPricingPeriodModalOpen = () => {
  dataLayerPush({
    event: 'extend_pricing_period_modal_open'
  })
}

const trackSetNewPricingModalOpen = () => {
  dataLayerPush({
    event: 'set_new_pricing_modal_open'
  })
}

const trackBagOrderButton = ({ location }) => {
  dataLayerPush({
    event: 'bag_order_button',
    location
  })
}

const trackSendBagButton = ({ location }) => {
  dataLayerPush({
    event: 'send_bag_button',
    location
  })
}

const trackContainerOrderHistoryButton = ({ location }) => {
  dataLayerPush({
    event: 'container_order_history_button',
    location
  })
}

/**
 * Tracking for the autocomplete suggestions and serachbox
 *
 * @param {string} searchTerm The query that the customer has wrote
 * @param {string} searchSource There are four type of search facets / indexs depending which recommendation the customer clicked on. Either category, brand, suggestedText (querysuggestions), text (plain search).
 */
const trackSearch = ({ searchTerm, searchSource }) => {
  dataLayerPush({
    event: 'search',
    search_term: searchTerm,
    search_source: searchSource
  })
}

/**
 * Tracking for the CLIP search
 * @param {string} searchTerm The query that the customer has wrote
 */
const trackCLIPSearch = ({ searchTerm }) => {
  dataLayerPush({
    event: 'search_clip',
    search_term: searchTerm
  })
}

let hitsViewedClickSource = null
/**
 * Tracking for the product ID search
 * @param {object} params
 * @param {string} params.productId
 * @param {string} params.itemId
 */
const trackClickSearchProductID = ({ productId, itemId }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'click_search_product_id',
    product_id: productId,
    ecommerce: {
      items: [itemId]
    }
  })
  hitsViewedClickSource = 'pdp_search_product_id'
}

const trackPickupOrder = ({ nrOfBags, page, title }) => {
  dataLayerPush({
    event: 'pickup_order',
    number_of_bags: nrOfBags,
    page,
    title
  })
}

const trackBagRating = ({ score, comment, improvementArea }) => {
  dataLayerPush({
    event: 'rate_bag',
    score,
    comment,
    improvement_area: improvementArea
  })
}

const trackP2pSaleInitiated = ({ itemId, value }) => {
  dataLayerPush({
    event: 'p2p_sale_start_initiated',
    item_id: itemId,
    p2p_listings: value
  })
}

const trackP2pSaleCompleted = ({ itemId, value }) => {
  dataLayerPush({
    event: 'p2p_sale_start_completed',
    item_id: itemId,
    p2p_listings: value
  })
}

const trackAddToWishList = ({ items, checkoutType }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'add_to_wishlist',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

const trackRemoveFromWishList = ({ items, checkoutType }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'remove_from_wishlist',
    checkout_type: checkoutType,
    ecommerce: {
      items
    }
  })
}

const trackAddTrackedSearch = ({ name }) => {
  dataLayerPush({
    event: 'add_tracked_search',
    tracked_search_name: name
  })
}

const trackReevaluationRequestStart = ({ itemId, saleId, clickSource, itemData }) => {
  dataLayerPush({
    event: 'reevaluation_request_started',
    click_source: clickSource,
    item_id: itemId,
    sale_id: saleId,
    ecommerce: {
      items: [itemData]
    }
  })
}

const trackReevaluationRequestComplete = ({
  itemId,
  saleId,
  returnIfRejected,
  clickSource,
  itemData
}) => {
  dataLayerPush({
    event: 'reevaluation_request_completed',
    click_source: clickSource,
    item_id: itemId,
    event_outcome: returnIfRejected === 'true' ? 'send_back' : 'donate',
    sale_id: saleId,
    ecommerce: {
      items: [itemData]
    }
  })
}

const trackSendBackRequestStart = ({ itemId, saleId, clickSource, itemData }) => {
  dataLayerPush({
    event: 'sendback_request_started',
    click_source: clickSource,
    item_id: itemId,
    sale_id: saleId,
    ecommerce: {
      items: [itemData]
    }
  })
}

const trackSendBackRequestCompleted = ({ itemIds, saleId, sendBackReason, clickSource }) => {
  dataLayerPush({
    event: 'sendback_request_completed',
    click_source: clickSource,
    sale_id: saleId,
    user_reason: sendBackReason,
    ecommerce: {
      items: itemIds
    }
  })
}

const trackAccordionClick = ({ accordionName }) => {
  dataLayerPush({
    event: 'accordion_click',
    accordion_name: accordionName
  })
}
const trackAccordionCTAClick = ({ accordionName, ctaName }) => {
  dataLayerPush({
    event: 'accordion_cta_click',
    accordion_name: accordionName,
    cta_name: ctaName
  })
}

const trackClickTile = ({ baseCategory, sliderType, tileName, sliderTitle, position, ...data }) => {
  dataLayerPush({
    event: 'tile_click',
    baseCategory,
    slider_type: sliderType,
    tileName,
    slider_title: sliderTitle,
    position,
    ...data
  })
  if (sliderType === 'recommended_item_types') {
    hitsViewedClickSource = 'recommended_item_types_tile_click'
  }
}

const trackViewItemSlider = ({ sliderId, sliderType, totalCount, position, ...data }) => {
  dataLayerPush({
    event: 'view_item_slider',
    slider_id: sliderId,
    slider_type: sliderType,
    total_count: totalCount,
    position,
    ...data
  })
}

const trackClickSliderItem = ({
  index,
  itemId,
  sliderId,
  sliderType,
  totalCount,
  position,
  ...data
}) => {
  dataLayerPush({
    event: 'slider_item_click',
    item_index: index,
    item_id: itemId,
    slider_id: sliderId,
    slider_type: sliderType,
    total_count: totalCount,
    position,
    ...data
  })
}

const trackEstimateSellabilityCalculation = ({ brand, type }) => {
  dataLayerPush({
    event: 'estimate_sellability_calculation',
    brand,
    type
  })
}

const trackItemPricingOpenModal = () => {
  dataLayerPush({
    event: 'item_pricing_open_modal'
  })
}

const trackSellpyFirstCheckoutSignup = () => {
  dataLayerPush({
    event: 'first_in_shipping_page'
  })
}

const trackItemPricingSelectedLength = ({ days, daysRemaining, price }) => {
  dataLayerPush({
    event: 'item_pricing_length_selected',
    days,
    daysRemaining,
    price
  })
}

const trackCreateSearchProfile = ({ clickSource, type }) => {
  dataLayerPush({
    event: 'create_search_profile',
    click_source_page: clickSource,
    type
  })
}

const trackCompleteSearchProfile = ({ clickSource, type }) => {
  dataLayerPush({
    event: 'complete_search_profile',
    click_source_page: clickSource,
    type
  })
}

const trackApplySearchProfile = ({ clickSource, type }) => {
  dataLayerPush({
    event: 'apply_search_profile',
    click_source_page: clickSource,
    type
  })
}

const trackNotificationGroupClick = ({ type, count }) => {
  dataLayerPush({
    event: 'notification_group_click',
    type,
    count
  })
}

const trackNotificationClick = ({ objectId, type, inGroup }) => {
  dataLayerPush({
    event: 'notification_click',
    type,
    notification_objectid: objectId,
    in_group: Boolean(inGroup)
  })
}

const trackImageZoomClick = () => {
  dataLayerPush({
    event: 'imageZoom_click'
  })
}

const trackFreeBagInCart = ({ available, checked }) => {
  dataLayerPush({
    event: 'free_bag_in_cart',
    available,
    checked
  })
}

const trackPlpPromotionalTile = ({ action, name }) => {
  dataLayerPush({
    event: 'plp_promotional_tile',
    name,
    action
  })
}

const trackNoStreetNumberPopup = () => {
  dataLayerPush({
    event: 'street_number_modal_opened'
  })
}

const trackHitsViewed = ({
  algoliaIndex,
  queryId,
  algoliaSearchHits,
  abTestID,
  abTestVariantID,
  personalisedSearchEnabled,
  similar_items_from_test,
  test_group_marker
}) => {
  const data = {
    event: 'hits_viewed',
    algolia_index: algoliaIndex,
    query_id: queryId,
    algolia_search_hits: algoliaSearchHits,
    algolia_test_id: abTestID,
    algolia_test_variant: abTestVariantID,
    personalised_ranking: personalisedSearchEnabled,
    pathname: window.location.pathname,
    similar_items_from_test,
    test_group_marker
  }
  if (hitsViewedClickSource) {
    data.click_source = hitsViewedClickSource
    hitsViewedClickSource = null
  }
  dataLayerPush(data)
}

const trackFulfillmentP2p = ({ action }) => {
  dataLayerPush({
    event: 'fulfillment_p2p',
    action
  })
}

const trackSimilarBrandsBannerClick = ({ brand }) => {
  dataLayerPush({
    event: 'add_similar_brands',
    brand
  })
}

const trackRateSaleContactCHTClick = ({ type, improvementArea, score, comment }) => {
  dataLayerPush({
    event: 'rate_sale_contact_cht_click',
    rating_type: type,
    rating_improvement_area: improvementArea,
    rating_score: score,
    rating_comment: comment
  })
}

const trackItemEditSubmit = ({ itemId, changes }) => {
  const previousEvents = window.gtmDataLayer?.filter(({ event }) => event === 'item_edit_submit')
  const dataLayerFlush = {}
  if (previousEvents?.length > 0) {
    const lastEvent = previousEvents.pop()
    Object.keys(lastEvent).forEach((key) => {
      if (key.includes('_change')) {
        dataLayerFlush[key] = undefined
      }
    })
  }
  dataLayerPush(dataLayerFlush)
  dataLayerPush({
    event: 'item_edit_submit',
    item_id: itemId,
    ...changes
  })
}

const trackRateClosedSaleButtonClickFromSalesOverview = ({ saleId }) => {
  dataLayerPush({
    event: 'rate_closed_sale_button_click_from_sales_overview',
    saleId
  })
}

const trackSimilarImageSearchClick = () => {
  dataLayerPush({
    event: 'click_similar_image_searchclick'
  })
}

const trackComplementaryItemsClick = () => {
  dataLayerPush({
    event: 'click_complementary_recommendations'
  })
}

const trackAwaitingApprovalListButtonClickFromSalesOverview = () => {
  dataLayerPush({
    event: 'awaiting_approval_list_button_click_from_sales_overview'
  })
}

const trackEndCheckoutClick = (data) => {
  dataLayerPush({
    event: 'end_checkout_click',
    ...data
  })
}

const trackViewsSustainability = () => {
  dataLayerPush({
    event: 'view_sustainability'
  })
}

const trackRequestChangesOpenModalClick = ({ location }) => {
  dataLayerPush({
    event: 'request_changes_open_modal_click',
    location
  })
}

const trackViewFavoriteModal = () => {
  dataLayerPush({
    event: 'view_favorite_modal'
  })
}

const trackViewFilterNudgePopover = () => {
  dataLayerPush({
    event: 'view_popover',
    popover_name: 'filter_nudge',
    popover_type: 'product_tour'
  })
}

const trackSalesScheduleModal = () => {
  dataLayerPush({
    event: 'open_modal_click',
    modal_name: 'seller_item_sales_schedule'
  })
}

const trackDAC7OpenInfo = () => {
  dataLayerPush({
    event: 'dac7_openInfo'
  })
}
const trackDAC7OpenForm = () => {
  dataLayerPush({
    event: 'dac7_openForm'
  })
}
const trackDAC7Completed = () => {
  dataLayerPush({
    event: 'dac7_completed'
  })
}

const trackDiscoverMoreClicks = ({ type }) => {
  dataLayerPush({
    event: 'discovermore_click',
    type
  })
}

const trackImageBrightnessAdjustment = ({ brightness, itemId }) => {
  dataLayerPush({ ecommerce: undefined })
  dataLayerPush({
    event: 'click_image_brightness',
    brightness,
    ecommerce: {
      items: [
        {
          item_id: itemId
        }
      ]
    }
  })
}

const trackShowMoreRecommendations = ({
  itemListId,
  itemListSubId,
  itemListName,
  visibleItemIds
}) => {
  dataLayerPush({
    event: 'show_more',
    item_list_id: itemListId,
    item_list_subid: itemListSubId,
    item_list_name: itemListName,
    visible_item_ids: visibleItemIds
  })
}

const trackClickMoreButton = ({ buttonType }) => {
  dataLayerPush({
    event: 'click_more_button',
    button_type: buttonType
  })
}

const trackToggleNegativeFilter = ({ attribute }) => {
  dataLayerPush({
    event: 'click_negative_filter',
    attribute: attribute
  })
}

const trackPDPConditionChip = () => {
  dataLayerPush({
    event: 'chip_click',
    chip_name: 'pdp_condition'
  })
}

const trackReevaluationConditionChip = () => {
  dataLayerPush({
    event: 'chip_click',
    chip_name: 'reevaluation_condition'
  })
}

const trackPDPDefectImageChip = () => {
  dataLayerPush({
    event: 'chip_click',
    chip_name: 'defect_image'
  })
}

const trackPersonalisedSearchToggle = ({ value }) => {
  dataLayerPush({
    event: value ? 'activate_personalised_ranking' : 'deactivate_personalised_ranking',
    personalised_ranking: value
  })
}
const trackButtonClickSell = ({ clickSource, clickDestination, buttonName }) => {
  dataLayerPush({
    event: 'button_click',
    click_source: clickSource,
    click_destination: clickDestination,
    button_name: buttonName
  })
}

const trackAddUserPreference = ({ category }) => {
  dataLayerPush({
    event: 'add_user_preference',
    preference_type: 'base_category',
    preference_selection: category
  })
}

const trackRemoveUserPreference = ({ category }) => {
  dataLayerPush({
    event: 'remove_user_preference',
    preference_type: 'base_category',
    preference_selection: category
  })
}

const trackSetFavoriteBrand = ({ brand, favorite, clickSourcePage }) => {
  dataLayerPush({
    event: favorite ? 'add_user_preference' : 'remove_user_preference',
    preference_type: 'brands',
    preference_selection: brand,
    click_source_page: clickSourcePage
  })
}

const trackApplySavedBrands = () => {
  dataLayerPush({
    event: 'apply_saved_brands'
  })
}

const trackContainerOrderAccordion = ({ accordionName, action, page }) => {
  dataLayerPush({
    event: 'accordion_click',
    accordion_name: accordionName,
    action,
    page
  })
}

const trackContainerOrderPage = ({ testGroupMarker }) => {
  dataLayerPush({
    event: 'view_bag_order_form',
    test_group_marker: testGroupMarker
  })
}

const trackSaleShareButton = ({ clickSource }) => {
  dataLayerPush({
    event: 'button_click',
    button_name: 'saleshare',
    click_source: clickSource,
    click_destination: '/sale-share'
  })
}

const trackShowProductDetailsButton = ({ showProductDetails }) => {
  dataLayerPush({
    event: 'button_click',
    button_name: 'show_product_details',
    show_product_details: showProductDetails,
    click_source: 'PDP'
  })
}

const trackMyShareFAQButtons = ({ buttonName, destination }) => {
  dataLayerPush({
    event: 'button_click',
    button_name: buttonName,
    click_source: '/my-share',
    click_destination: destination
  })
}

const trackMyShareFAQAccordions = ({ accordionName }) => {
  dataLayerPush({
    event: 'accordion_click',
    accordion_name: accordionName,
    page: '/my-share'
  })
}

const trackPerceivedSizeViewed = ({
  itemId,
  perceivedSizeValue,
  perceivedSizeSource,
  perceivedSizeAvailable,
  testGroupMarker
}) => {
  dataLayerPush({
    event: 'perceived_size_viewed',
    item_id: itemId,
    perceived_size_value: perceivedSizeValue,
    perceived_size_source: perceivedSizeSource,
    perceived_size_available: perceivedSizeAvailable,
    test_group_marker: testGroupMarker
  })
}

const trackHowItWorksNavigation = ({ ButtonName, clickDestination }) => {
  dataLayerPush({
    event: 'button_click',
    button_name: ButtonName,
    click_source: '/how-it-works',
    click_destination: clickDestination
  })
}

const trackPackTipsCategoryClick = ({ categoryName }) => {
  dataLayerPush({
    event: 'pack_tips_category_click',
    category_name: categoryName
  })
}

const trackContainerPush = ({ clickSource }) => {
  dataLayerPush({
    event: 'button_click',
    button_name: 'container_push',
    click_source: clickSource,
    click_destination: '/order-bag'
  })
}

const trackAppDownloadClick = ({ target, location }) => {
  dataLayerPush({
    event: 'button_click',
    button_name: 'app_download',
    click_source: location,
    click_destination: target
  })
}

export const DataLayer = {
  trackPermissionAnalytics,
  trackPermissionExternalMarketing,
  trackPermissionEmailMarketing,
  trackSetUserId,
  trackUnsetUserId,
  trackItemView,
  trackCartView,
  trackItemClick,
  trackAddToCart,
  trackRemoveFromCart,
  trackPromotionClick,
  trackMenuNavigation,
  trackHomeTabNavigation,
  trackMapDrawer,
  trackGiftCardAddToCart,
  trackGiftCardRemoveFromCart,
  trackBeginCheckout,
  trackBeginCheckoutV2,
  trackCheckoutViewAddEmail,
  trackCheckoutAddEmailInfo,
  trackCheckoutAddPromoCode,
  trackCheckoutViewAddAddress,
  trackCheckoutAddShippingAddress,
  trackCheckoutViewReviewOrder,
  trackCheckoutReviewOrder,
  trackCheckoutViewAddShipping,
  trackCheckoutAddShippingInfo,
  trackCheckoutAddP2PShippingInfo,
  trackCheckoutViewPaymentInfo,
  trackCheckoutAddPaymentInfo,
  trackViewCreditConversion,
  trackCreditConversion,
  trackOptInAutomaticTransfers,
  trackAutomaticPayoutSettings,
  trackPurchase,
  trackPurchaseV2,
  trackRefund,
  trackFakeDoorClick,
  trackGiftCardRedeemed,
  trackLogin,
  trackSignup,
  trackSearch,
  trackCLIPSearch,
  trackClickSearchProductID,
  trackPickupOrder,
  trackBagRating,
  trackP2pSaleInitiated,
  trackP2pSaleCompleted,
  trackAddToWishList,
  trackRemoveFromWishList,
  trackAddTrackedSearch,
  trackReevaluationRequestStart,
  trackReevaluationRequestComplete,
  trackSendBackRequestStart,
  trackSendBackRequestCompleted,
  trackClickTile,
  trackSetUserEmail,
  trackUnsetUserEmail,
  trackSetUserPhone,
  trackUnsetUserPhone,
  trackViewItemSlider,
  trackClickSliderItem,
  trackEstimateSellabilityCalculation,
  trackItemPricingOpenModal,
  trackSellpyFirstCheckoutSignup,
  trackItemPricingSelectedLength,
  trackCreateSearchProfile,
  trackCompleteSearchProfile,
  trackApplySearchProfile,
  trackNotificationGroupClick,
  trackNotificationClick,
  trackImageZoomClick,
  trackFreeBagInCart,
  trackPlpPromotionalTile,
  trackNoStreetNumberPopup,
  trackHitsViewed,
  trackOrderPickupButton,
  trackDropoffButton,
  trackBuyingNudgeExploreButton,
  trackBuyingNudgeCloseButton,
  trackBagOrderButton,
  trackContainerOrderHistoryButton,
  trackFulfillmentP2p,
  trackAccordionClick,
  trackSimilarBrandsBannerClick,
  trackCommencePreparedItemSale,
  trackExtendPricingPeriodModalOpen,
  trackSetNewPricingModalOpen,
  trackRateSaleContactCHTClick,
  trackItemEditSubmit,
  trackRateClosedSaleButtonClickFromSalesOverview,
  trackSimilarImageSearchClick,
  trackComplementaryItemsClick,
  trackEndCheckoutClick,
  trackAwaitingApprovalListButtonClickFromSalesOverview,
  trackViewsSustainability,
  trackRequestChangesOpenModalClick,
  trackViewFavoriteModal,
  trackSalesScheduleModal,
  trackDAC7OpenInfo,
  trackDAC7OpenForm,
  trackDAC7Completed,
  trackDiscoverMoreClicks,
  trackImageBrightnessAdjustment,
  trackClickMoreButton,
  createDataLayerEvent,
  trackToggleNegativeFilter,
  trackPDPConditionChip,
  trackReevaluationConditionChip,
  trackPDPDefectImageChip,
  trackShowMoreRecommendations,
  trackPersonalisedSearchToggle,
  trackAddUserPreference,
  trackRemoveUserPreference,
  trackSetFavoriteBrand,
  trackApplySavedBrands,
  trackViewFilterNudgePopover,
  trackContainerOrderAccordion,
  trackContainerOrderPage,
  trackButtonClickSell,
  trackSaleShareButton,
  trackMyShareFAQButtons,
  trackShowProductDetailsButton,
  trackMyShareFAQAccordions,
  trackPerceivedSizeViewed,
  trackAccordionCTAClick,
  trackHowItWorksNavigation,
  trackPackTipsCategoryClick,
  trackSendBagButton,
  trackContainerPush,
  trackAppDownloadClick
}
